/**
 * useReservationsList — state for the Reservations tab (BON-382).
 *
 * Deliberately simpler than `useBookingsListState`: no paging, no sort, no
 * status filter. Reservations are few and short-lived, and the server returns
 * the whole current set ordered newest-first. That is also what lets this tab
 * exist at all without touching the bookings list's frozen DTO or its
 * database-side paging.
 *
 * Search filters CLIENT-SIDE for the same reason — the set is already in hand,
 * so a round-trip per keystroke would buy nothing. The history toggle does NOT:
 * the finished rows are not in hand, and fetching them by default is precisely
 * what the tab stopped doing (BON-472).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  listReservations,
  type ReservationListResult,
  type ReservationRow,
} from '../../api/reservations';

import { subscribeToBookingsRefresh } from './bookingsRefreshBus';

export interface ReservationsListState {
  reservations: ReservationRow[];
  seatsHeld: number;
  outstandingCount: number;
  loading: boolean;
  error: string | null;
  search: string;
  setSearch: (v: string) => void;
  /** Whether finished reservations are being shown. Off is the default view. */
  includeInactive: boolean;
  setIncludeInactive: (v: boolean) => void;
  refresh: () => void;
}

function matches(r: ReservationRow, needle: string): boolean {
  if (!needle) return true;
  const hay = [r.customerName, r.customerEmail, r.reference, r.workshopName, r.venueName]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return hay.includes(needle);
}

export function useReservationsList(): ReservationsListState {
  const [data, setData] = useState<ReservationListResult>({
    reservations: [],
    seatsHeld: 0,
    outstandingCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);

  // THE TOGGLE IS PART OF THE FETCH, not a post-filter, so every re-read carries
  // it: an action's refresh, the refresh bus, and the effect below all go
  // through here. A refresh that dropped the flag would silently snap a CSR
  // reading history back to the active view the moment they resent a link.
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await listReservations(includeInactive));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load reservations.');
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    void load();
  }, [load]);

  // A booking created from anywhere — this page's CTA, the palette, the
  // keyboard shortcut — should appear here without a manual refresh. Only a
  // reservation clears the search (BON-628): a comp doesn't touch this list,
  // so it must not wipe a search the CSR is mid-typing on this tab.
  useEffect(
    () =>
      subscribeToBookingsRefresh((event) => {
        if (event.kind === 'reservation') setSearch('');
        void load();
      }),
    [load],
  );

  const needle = search.trim().toLowerCase();
  const reservations = useMemo(
    () => data.reservations.filter((r) => matches(r, needle)),
    [data.reservations, needle],
  );

  return {
    reservations,
    // Totals come from the SERVER, never from the rows on screen: "15 seats
    // held" is a fact about the workshops, not about what the CSR typed or which
    // view they chose. Counting the rendered rows would make the figure jump the
    // moment the history toggle revealed a page of finished reservations.
    seatsHeld: data.seatsHeld,
    outstandingCount: data.outstandingCount,
    loading,
    error,
    search,
    setSearch,
    includeInactive,
    setIncludeInactive,
    refresh: () => void load(),
  };
}
